<template>
  <div class="select-none">
    <!-- ── Expanded: a vertically scrolling run of months ──
         Scroll down to keep walking forward through the calendar; swipe sideways (or use
         the arrows) to jump a whole month at a time. -->
    <div
      v-if="expanded"
      class="bg-white border border-gray-200 rounded-2xl p-3 shadow-sm"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <div class="flex items-center justify-between px-1 pb-2">
        <!-- RTL: right arrow = previous month, left arrow = next month -->
        <button type="button" class="p-1.5" @click="stepMonth(1)">
          <i class="pi pi-chevron-right !text-xs !text-gray-500" />
        </button>
        <span class="text-sm font-semibold">{{ monthLabel }}</span>
        <button type="button" class="p-1.5" @click="stepMonth(-1)">
          <i class="pi pi-chevron-left !text-xs !text-gray-500" />
        </button>
      </div>

      <!-- Weekday header sits above the scroll area, so it stays put while months pass under it -->
      <div class="grid grid-cols-7 gap-y-1 pb-1 border-b border-gray-100 mb-1">
        <div v-for="d in DOW_MONTH" :key="d" class="text-center text-[11px] text-gray-400">{{ d }}</div>
      </div>

      <div
        ref="scroller"
        class="max-h-[54vh] overflow-y-auto overscroll-contain scrollbar-none"
        @scroll.passive="onScroll"
      >
        <div
          v-for="month in months"
          :key="month.key"
          :ref="el => setMonthEl(month.key, el)"
          class="pb-1"
        >
          <div class="text-[11.5px] font-semibold text-gray-500 py-1.5 px-1">{{ month.label }}</div>
          <div class="grid grid-cols-7 gap-y-1">
            <template v-for="(cell, i) in month.cells" :key="i">
              <div v-if="!cell" />
              <div v-else class="flex justify-center">
                <div
                  class="w-9 h-9 flex flex-col items-center justify-center rounded-xl cursor-pointer transition-colors"
                  :class="[
                    cell.selected ? 'bg-primary' : '',
                    cell.isToday && !cell.selected ? 'ring-1 ring-primary ring-inset' : '',
                    cell.isToday && cell.selected ? 'ring-2 ring-primary/30 ring-offset-1' : ''
                  ]"
                  @click="pick(cell.date)"
                >
                  <span
                    class="text-[13px]"
                    :class="cell.selected
                      ? 'text-white font-semibold'
                      : (cell.isToday ? 'text-primary font-semibold' : 'text-gray-800')"
                  >{{ cell.day }}</span>
                  <span class="h-1.5 flex items-center">
                    <span v-if="cell.hasEvents" class="w-1 h-1 rounded-full" :class="cell.selected ? 'bg-white' : 'bg-primary'" />
                  </span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Collapsed: week strip (swipe left/right to change week) ── -->
    <div
      v-else
      class="flex gap-1"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <div
        v-for="cell in weekCells"
        :key="cell.iso"
        class="flex-1 flex flex-col items-center gap-1 cursor-pointer"
        @click="pick(cell.date)"
      >
        <div
          class="w-12 h-[60px] flex flex-col items-center justify-center rounded-xl transition-colors"
          :class="[
            cell.selected ? 'bg-primary' : '',
            cell.isToday && !cell.selected ? 'ring-1 ring-primary ring-inset' : '',
            cell.isToday && cell.selected ? 'ring-2 ring-primary/30 ring-offset-1' : ''
          ]"
        >
          <span class="text-[11px]" :class="cell.selected ? 'text-primary-300 font-medium' : (cell.isToday ? 'text-primary font-medium' : 'text-gray-400')">{{ cell.dow }}</span>
          <span class="text-base" :class="cell.selected ? 'text-white' : (cell.isToday ? 'text-primary font-semibold' : 'text-gray-800')">{{ cell.day }}</span>
          <span class="h-2 flex items-center">
            <span v-if="cell.hasEvents" class="w-1 h-1 rounded-full" :class="cell.selected ? 'bg-white' : 'bg-primary'" />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { DateTime } from 'luxon';

const props = defineProps<{
  modelValue: Date;       // selected date (highlight)
  viewDate: Date;         // anchor: which week/month is shown
  expanded: boolean;
  eventDates: Set<string>; // ISO dates (yyyy-MM-dd) that have appointments
}>();
const emit = defineEmits<{ 'update:modelValue': [d: Date]; 'update:viewDate': [d: Date] }>();

const ZONE = 'Asia/Jerusalem';
const DOW_STRIP = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];
const DOW_MONTH = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
const HEBREW_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

/** Months rendered on each side of the anchor to start with, and how many to add on demand. */
const INITIAL_SPAN = 6;
const EXTEND_BY = 6;
/** Distance from an edge (px) at which the next batch of months is added. */
const EXTEND_THRESHOLD = 400;

const anchor = computed(() => DateTime.fromJSDate(props.viewDate).setZone(ZONE, { keepLocalTime: true }).startOf('day'));
const selectedIso = computed(() => DateTime.fromJSDate(props.modelValue).toISODate());
const todayIso = DateTime.local({ zone: ZONE }).toISODate();

// Sunday-based weekday index (0 = Sunday … 6 = Saturday).
const sundayIndex = (dt: DateTime) => dt.weekday % 7;

const monthLabel = computed(() => `${HEBREW_MONTHS[anchor.value.month - 1]} ${anchor.value.year}`);

const hasEvents = (iso: string | null) => !!iso && props.eventDates.has(iso);

const weekCells = computed(() => {
  const sunday = anchor.value.minus({ days: sundayIndex(anchor.value) });
  return Array.from({ length: 7 }, (_, i) => {
    const d = sunday.plus({ days: i });
    const iso = d.toISODate()!;
    return {
      iso, day: d.day, dow: DOW_STRIP[i], date: d.toJSDate(),
      selected: iso === selectedIso.value,
      isToday: iso === todayIso,
      hasEvents: hasEvents(iso),
    };
  });
});

// ── The scrolling run of months ──
// `first`/`count` describe the rendered window; it grows in both directions as the
// owner scrolls, so browsing never dead-ends at an arbitrary boundary.
const monthKey = (dt: DateTime) => dt.toFormat('yyyy-LL');
const firstMonth = ref<DateTime>(anchor.value.startOf('month').minus({ months: INITIAL_SPAN }));
const monthCount = ref(INITIAL_SPAN * 2 + 1);

const buildCells = (monthStart: DateTime) => {
  const lead = sundayIndex(monthStart);
  const daysInMonth = monthStart.daysInMonth ?? 30;
  const cells: Array<null | { day: number; date: Date; selected: boolean; isToday: boolean; hasEvents: boolean }> = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = monthStart.set({ day: d });
    const iso = dt.toISODate()!;
    cells.push({
      day: d,
      date: dt.toJSDate(),
      selected: iso === selectedIso.value,
      isToday: iso === todayIso,
      hasEvents: hasEvents(iso),
    });
  }
  return cells;
};

const months = computed(() =>
  Array.from({ length: monthCount.value }, (_, i) => {
    const start = firstMonth.value.plus({ months: i });
    return {
      key: monthKey(start),
      label: `${HEBREW_MONTHS[start.month - 1]} ${start.year}`,
      cells: buildCells(start),
    };
  })
);

const scroller = ref<HTMLElement | null>(null);
const monthEls = new Map<string, HTMLElement>();
const setMonthEl = (key: string, el: unknown) => {
  if (el instanceof HTMLElement) monthEls.set(key, el);
  else monthEls.delete(key);
};

/** Scroll a month to the top of the run. `smooth` is off for the initial positioning. */
const scrollToMonth = async (dt: DateTime, smooth = true) => {
  await nextTick();
  const el = monthEls.get(monthKey(dt));
  if (!el || !scroller.value) return;
  scroller.value.scrollTo({ top: el.offsetTop, behavior: smooth ? 'smooth' : 'auto' });
};

// Opening the picker lands on the month being viewed rather than at the top of the run.
watch(() => props.expanded, async (isOpen) => {
  if (!isOpen) return;
  firstMonth.value = anchor.value.startOf('month').minus({ months: INITIAL_SPAN });
  monthCount.value = INITIAL_SPAN * 2 + 1;
  await scrollToMonth(anchor.value, false);
}, { immediate: true });

// The month the run is currently showing, reported up (debounced) so the header label and
// the calendar's own month loading follow the scroll.
let scrollTimer: ReturnType<typeof setTimeout> | undefined;
const reportVisibleMonth = () => {
  const el = scroller.value;
  if (!el) return;
  // The month whose block covers the top edge of the viewport, with a small bias so a
  // month counts as "current" as soon as its header is comfortably in view.
  const probe = el.scrollTop + 24;
  let current: { key: string; top: number } | undefined;
  for (const [key, monthEl] of monthEls) {
    if (monthEl.offsetTop <= probe && (!current || monthEl.offsetTop > current.top)) {
      current = { key, top: monthEl.offsetTop };
    }
  }
  if (!current) return;
  const [year, month] = current.key.split('-').map(Number);
  if (year === anchor.value.year && month === anchor.value.month) return;
  emit('update:viewDate', DateTime.fromObject({ year, month, day: 1 }, { zone: ZONE }).toJSDate());
};

const extendIfNeeded = () => {
  const el = scroller.value;
  if (!el) return;
  if (el.scrollHeight - el.scrollTop - el.clientHeight < EXTEND_THRESHOLD) {
    monthCount.value += EXTEND_BY;
    return;
  }
  if (el.scrollTop < EXTEND_THRESHOLD) {
    // Prepending shifts everything down, so restore the visual position afterwards —
    // otherwise the content jumps out from under the finger.
    const before = el.scrollHeight;
    firstMonth.value = firstMonth.value.minus({ months: EXTEND_BY });
    monthCount.value += EXTEND_BY;
    nextTick(() => {
      if (!scroller.value) return;
      scroller.value.scrollTop += scroller.value.scrollHeight - before;
    });
  }
};

const onScroll = () => {
  extendIfNeeded();
  if (scrollTimer) clearTimeout(scrollTimer);
  scrollTimer = setTimeout(reportVisibleMonth, 120);
};

onBeforeUnmount(() => {
  if (scrollTimer) clearTimeout(scrollTimer);
});

const pick = (date: Date) => emit('update:modelValue', date);

// Month arrows / horizontal swipe browse a month at a time: scroll the run to that month
// and report it up (the user still taps a day to select one).
const stepMonth = (dir: -1 | 1) => {
  const target = anchor.value.plus({ months: dir }).startOf('month');
  emit('update:viewDate', target.toJSDate());
  if (props.expanded) {
    // Keep at least one more month rendered in the direction of travel.
    if (dir === 1) monthCount.value += 1;
    else if (target < firstMonth.value) firstMonth.value = target.minus({ months: 1 });
    scrollToMonth(target);
  }
};

// A week-strip swipe advances the whole view: move the selected date by ±1 week so the
// strip and the calendar body move together.
const stepWeek = (dir: -1 | 1) =>
  emit('update:modelValue', DateTime.fromJSDate(props.modelValue).plus({ weeks: dir }).toJSDate());

// Swipe handling. RTL convention: swipe right → next (forward in time), swipe left → previous.
// Vertical movement is left to the scroll container.
let startX = 0, startY = 0;
const onTouchStart = (e: TouchEvent) => { const t = e.changedTouches[0]; startX = t.clientX; startY = t.clientY; };
const onTouchEnd = (e: TouchEvent) => {
  const t = e.changedTouches[0];
  const dx = t.clientX - startX;
  const dy = t.clientY - startY;
  if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
  const dir: -1 | 1 = dx > 0 ? 1 : -1; // right swipe = next
  props.expanded ? stepMonth(dir) : stepWeek(dir);
};
</script>

<style scoped lang="scss">
/* The month run is driven by touch/scroll gestures; the bar itself would only crowd
   the narrow grid. */
.scrollbar-none {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
